
import { useState, useEffect, useRef } from 'react';
import { fetchRoadBarriers, fetchOSMBarriers, fetchSwedishBarriers } from '../services/nvdbService';
import { RoadBarrier, Coordinates, MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';
import type { SupportedCountry } from '../utils/countryDetection';

export const useRoadBarriers = (
  active: boolean,
  zoom: number,
  currentMapCenter: Coordinates,
  currentViewBounds: MapBounds | null,
  viewportCountry?: SupportedCountry | null,
  swedenEnabled?: boolean,
) => {
  const [barriers, setBarriers] = useState<RoadBarrier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);
  const lastCountryRef = useRef<SupportedCountry | null | undefined>(undefined);

  useEffect(() => {
    if (!active || zoom < 13 || !currentViewBounds) {
      if (!active) { setBarriers([]); setError(false); lastFetchedBoundsRef.current = null; }
      return;
    }

    // Re-fetch if country changed even with same bounds
    const countryChanged = lastCountryRef.current !== viewportCountry;
    if (!countryChanged && lastFetchedBoundsRef.current && boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const loadBarriers = async () => {
      setIsLoading(true);
      try {
        const promises: Promise<RoadBarrier[]>[] = [];

        // Norwegian NVDB barriers (only when in Norway or unknown/border area)
        if (viewportCountry === 'NO' || viewportCountry === null || viewportCountry === undefined) {
          promises.push(fetchRoadBarriers(currentViewBounds));
        }

        // Swedish Trafikverket barriers (only when in Sweden AND Sweden is enabled)
        if ((viewportCountry === 'SE' || viewportCountry === null) && swedenEnabled) {
          promises.push(fetchSwedishBarriers(currentViewBounds));
        }

        // OSM barriers always (global supplement)
        promises.push(fetchOSMBarriers(currentViewBounds));

        const results = await Promise.all(promises);
        setBarriers(results.flat());
        setError(false);
        lastFetchedBoundsRef.current = currentViewBounds;
        lastCountryRef.current = viewportCountry;
      } catch (err) {
        console.error("Failed to load road barriers:", err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(loadBarriers, 700);
    return () => clearTimeout(timer);
  }, [currentViewBounds, active, zoom, viewportCountry, swedenEnabled]);

  return { barriers, isLoading, error };
};
